import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { Form, InputGroup, Table } from 'react-bootstrap';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import DeviceScanPanel from '../../components/Devices/DeviceScanPanel';
import { searchOnEnter } from '../../components/searchOnEnter';
import { Prod01 } from '../../constants/ActionType';
import { formatDob } from '../../utils/dateFormat';
import { useSortableTable, SortableTh, personName } from '../../components/SortableTable';

const PICKER_SORT_COLUMNS = {
    name: { type: 'name', value: personName },
    emrId: { type: 'string', value: (p) => p?.emrId },
    dob: { type: 'date', value: (p) => p?.DOB },
};

// Assign / unassign devices by barcode scan (Enrollment Coordinator, Org Manager, Super Admin, Nurse Lead).
const DeviceAssign = ({ location }) => {
    const alert = useAlert();
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [patient, setPatient] = useState(location?.state?.patient || null);
    const pickerSort = useSortableTable(results, { id: 'devices.assignPatientPicker', columns: PICKER_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });

    const find = useCallback(async (term) => {
        const { data } = await axios.get(`${Prod01}/inventory/patients`, { params: { search: term, limit: 20 } });
        return data.patients || [];
    }, []);

    const runSearch = async (v) => {
        try {
            setResults(await find(String(v || '').trim()));
        } catch (err) {
            alert.error(err?.response?.data?.message || err.message);
        }
    };

    const refreshPatient = useCallback(async () => {
        if (!patient?._id) return;
        try {
            const { data } = await axios.get(`${Prod01}/patients/patientprofile/${patient._id}`);
            if (data && data._id === patient._id) setPatient(data);
        } catch (err) {
            alert.error(err?.response?.data?.message || err.message);
        }
    }, [patient?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        refreshPatient();
    }, [refreshPatient]);

    return (
        <>
            <MetaData title="Assign / Unassign Devices" />
            <RoleShell title="Assign / Unassign Devices">
                <h5 style={{ color: '#004aad' }}>
                    <i className="fas fa-barcode"></i> <strong>&nbsp;Assign / Unassign Devices</strong>
                </h5>
                <hr />
                <div className="row">
                    <div className="col-md-5">
                        <label className="form-label"><strong>1. Patient</strong></label>
                        <InputGroup className="mb-2">
                            <Form.Control
                                placeholder="Patient name or EHR-ID, then Enter"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={searchOnEnter(runSearch)}
                            />
                            <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => runSearch(search)}>Find</button>
                        </InputGroup>
                        {results.length ? (
                            <Table className="table table-sm table-hover">
                                <thead>
                                    <tr>
                                        <SortableTh sortKey="name" sort={pickerSort}>Name</SortableTh>
                                        <SortableTh sortKey="emrId" sort={pickerSort}>EHR ID</SortableTh>
                                        <SortableTh sortKey="dob" sort={pickerSort}>DOB</SortableTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pickerSort.rows.map((p) => (
                                        <tr key={p._id} style={{ cursor: 'pointer' }} onClick={() => { setPatient(p); setResults([]); }}>
                                            <td>{p.lastname}, {p.firstname}</td>
                                            <td>{p.emrId}</td>
                                            <td>{formatDob(p.DOB)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}
                        {patient ? (
                            <div className="p-2 rounded" style={{ background: '#eef4ff' }}>
                                <strong>{patient.lastname}, {patient.firstname}</strong>
                                <div><small>EHR-ID {patient.emrId} {patient.DOB ? `· DOB ${formatDob(patient.DOB)}` : ''}</small></div>
                            </div>
                        ) : <small className="text-muted">Select the patient first.</small>}
                    </div>
                    <div className="col-md-7">
                        <label className="form-label"><strong>2. Device</strong></label>
                        {patient ? <Link className="btn btn-link btn-sm px-0" to={{ pathname: '/patientProfile', state: { patientid: patient._id } }} data-testid="back-to-profile">← Back to patient profile</Link> : null}
                        {patient ? <DeviceScanPanel patient={patient} onChanged={refreshPatient} /> : null}
                    </div>
                </div>
            </RoleShell>
        </>
    );
};

export default DeviceAssign;
